/*
 * Developed for EPSON AMERICA INC. by Softeq Development Corporation
 * http://www.softeq.com
 */

import { compose, lifecycle, withHandlers, withState } from 'recompose'
import { connect } from 'react-redux'
import { getFormValues, isValid } from 'redux-form'
import _get from 'lodash/get'
import _omit from 'lodash/omit'
import { analytics } from 'services/Analytics'
import { ERROR_CODES } from 'constants/errors'
import { ALERTS } from 'constants/alerts'
import { CARD_FIELD_NAMES, USER_FORMS } from 'constants/forms'
import { withLocalization } from 'hocs/localization'
import { onRequestToPaymetric } from 'redux-app/actionCreators/users'
import { openModal } from 'redux-app/modules/modals'
import { onShowError, createAlert } from 'redux-app/modules/alerts'
import { onCompleteInitialSubscription, onSaveInitialSubscription } from 'redux-app/modules/auth'
import { getUserDetails } from 'api/client/actions/userProfile'
import { isCaptchaEnabled, isCaptchaWhiteListed } from 'redux-app/selectors/apiData'
import { getRegistrationStep } from 'redux-app/selectors/auth'
import { getUserId, isCaptchaRequiredForCreditCardChange } from 'redux-app/selectors/user'
import { MODAL_PAYMETRIC_SERVICE_UNAVAILABLE } from 'apps/Profile/PaymetricServiceUnavailableModal'
import { correctedAllAddressesModal } from 'apps/Profile/CorrectedAddressModal'
import { getErrorCodes, getNormalizedValidationErrors } from 'utils/error'
import SignUpForms from './SignUpForms'
import { getNormalizedAddress } from './utils'
import { getSubscriptionPlans, getSelectedPlanId } from 'redux-app/selectors/subscription'

interface IProps {
  getUserDetails: () => Promise<{}>
}

const isCreateAccountFormsValid = (state, props) => {
  return isValid(USER_FORMS.profileShippingForm)(state) &&
    isValid(USER_FORMS.profileBillingForm)(state) &&
    isValid(USER_FORMS.profileAddCard)(state) &&
    (!props.isBusinessCheckboxChecked || isValid(USER_FORMS.profileBusinessDetails)(state))
}

const onShowAddressFormsError = (error: any, alertName) => dispatch => {
  const codes = getErrorCodes(error)
  if (codes.includes(ALERTS.addressIsNotValid)) {
    dispatch(createAlert({
      name: alertName,
    }))
  } else {
    onShowError(error)
  }
}

export default compose(
  withLocalization('profile.create'),
  withState('isPaymentDetailsOpened', 'setPaymentDetailsVisibility', false),
  withState('isBillingCheckboxChecked', 'setBillingCheckbox', true),
  withState('isBusinessCheckboxChecked', 'setBusinessCheckbox', false),
  withState('isSubmitting', 'setSubmittingState', false),
  connect(
    (state, props) => {
      return {
        isButtonDisabled: !isCreateAccountFormsValid(state, props),
        userId: getUserId(state),
        step: getRegistrationStep(state),
        shippingInfoFormValues: getFormValues(USER_FORMS.profileShippingForm)(state),
        businessInfoFormValues: getFormValues(USER_FORMS.profileBusinessDetails)(state),
        cardInfoFormValues: getFormValues(USER_FORMS.profileAddCard)(state),
        billingInfoFormValues: getFormValues(USER_FORMS.profileBillingForm)(state),
        isShippingFormValid: isValid(USER_FORMS.profileShippingForm)(state),
        plan: getSubscriptionPlans(state).find(plan => plan.id === getSelectedPlanId(state)),
        isCaptchaEnabled: isCaptchaEnabled(state) && isCaptchaRequiredForCreditCardChange(state),
        isCaptchaWhiteListed: isCaptchaWhiteListed(state),
      }
    },
    {
      onRequestToPaymetric,
      getUserDetails,
      onSaveInitialSubscription,
      onCompleteInitialSubscription,
      openModal,
      onShowError,
      onShowAddressFormsError,
      createAlert,
    },
  ),
  lifecycle<IProps, any>({
    componentWillMount () {
      const { getUserDetails } = this.props
      getUserDetails()
        .then(() => {
          this.setState({
            loadProgress: 100,
          })
        })
        .catch(() => {
          this.setState({
            loadProgress: 100,
          })
        })
        .finally(() => {
          analytics.openCreatePaymentAndShippingPage()
        })
    },
    componentWillReceiveProps ({ isShippingFormValid }) {
      if (this.props.isShippingFormValid !== isShippingFormValid && isShippingFormValid) {
        this.props.setPaymentDetailsVisibility(true)
      }
    },
  }),
  withHandlers({
    changeBillingCheckbox: ({ isBillingCheckboxChecked, setBillingCheckbox }) => () => {
      return setBillingCheckbox(!isBillingCheckboxChecked)
    },
    changeBusinessCheckbox: ({ isBusinessCheckboxChecked, setBusinessCheckbox }) => () => {
      return setBusinessCheckbox(!isBusinessCheckboxChecked)
    },
    submitForm: ({
      userId,
      onRequestToPaymetric,
      shippingInfoFormValues,
      businessInfoFormValues,
      cardInfoFormValues,
      billingInfoFormValues,
      onSaveInitialSubscription,
      onCompleteInitialSubscription,
      openModal,
      onShowError,
      createAlert,
      onShowAddressFormsError,
      setSubmittingState,
      isCaptchaEnabled,
      isCaptchaWhiteListed,
    }) => () => {

      if (isCaptchaEnabled && !isCaptchaWhiteListed && !cardInfoFormValues.captcha) {
        return Promise.resolve()
      }

      setSubmittingState(true)
      const paymetricRequestData = {
        paymentCreditCard: _get(cardInfoFormValues, CARD_FIELD_NAMES.paymentCreditCard).replace(/-/g, ''),
        expMonth: _get(cardInfoFormValues, CARD_FIELD_NAMES.expMonth),
        expYear: _get(cardInfoFormValues, CARD_FIELD_NAMES.expYear),
        CVV: _get(cardInfoFormValues, CARD_FIELD_NAMES.CVV),
      }

      return onRequestToPaymetric(paymetricRequestData, cardInfoFormValues.captcha === undefined ? '' : cardInfoFormValues.captcha)
      .then(({ accessToken }) => {
        return onSaveInitialSubscription({
          userId,
          billingAddress: getNormalizedAddress({
            ...billingInfoFormValues,
            countryId: 1, // USA hardcoded
          }),
          company: businessInfoFormValues || null,
          shippingAddress: getNormalizedAddress({
            ...shippingInfoFormValues,
            countryId: 1, // USA hardcoded
          }),
          creditCard: {
            paymentAccessToken: accessToken,
            cardHolderName: _get(cardInfoFormValues, CARD_FIELD_NAMES.nameOnCard).trim(),
            PreAuthCompleted: false,
          },
        })
          .then(({ value: { result: { shippingAddress, creditCard: { billingAddress } } } }) => {
            setSubmittingState(false)
            if (!shippingAddress.isMatch || !billingAddress.isMatch) {
              const data = {}
              if (!shippingAddress.isMatch) {
                data.shippingCorrectedResult = _omit(shippingAddress, ['isMatch'])
              }
              if (!billingAddress.isMatch) {
                data.billingCorrectedResult = _omit(billingAddress, ['isMatch'])
              }
              openModal({ name: correctedAllAddressesModal.name, data })
              return
            }
            return onCompleteInitialSubscription().finally(() => {
              setSubmittingState(false)
            })
          })
      })
      .catch((error: any) => {
        setSubmittingState(false)

        if (isCaptchaEnabled && !isCaptchaWhiteListed)
        {
            window.grecaptcha.reset()
            cardInfoFormValues.captcha = ''
        }

        const errorCodes = getErrorCodes(error)
        analytics.showPaymentAndShippingError({
          errorCodes: getNormalizedValidationErrors(error.result).map(({ key, code }) => {
            if (code === ERROR_CODES.addressIsNotValid) {
              return `${code}.${key}`
            }
            return code
          }),
        })
        if (error.status === 409 && errorCodes.includes(ERROR_CODES.paymetricServiceUnavailable)) {
          openModal({
            name: MODAL_PAYMETRIC_SERVICE_UNAVAILABLE,
            data: {
              requestData: {
                ...cardInfoFormValues,
              },
            },
          })
          return
        }
        else if (errorCodes.includes(ERROR_CODES.paymetricCardCheckFailed)) {
           createAlert({ name: ALERTS.profileCreditCardIssue })
           return
        }
        if (errorCodes.includes(ERROR_CODES.addressIsNotValid)) {
          const normalizedValidationErrors = getNormalizedValidationErrors(error.result)
          normalizedValidationErrors.forEach(({ code, key }) => {
            if (code === ERROR_CODES.addressIsNotValid) {
              key.includes('ShippingAddress') && onShowAddressFormsError(error, ALERTS.profileShippingFormError)
              key.includes('BillingAddress') && onShowAddressFormsError(error, ALERTS.profileBillingFormError)
            }
          })
          return
        }
        onShowError(error, cardInfoFormValues)
      })
    },
    onConfirmCorrectedAddress: ({
      userId,
      businessInfoFormValues,
      cardInfoFormValues,
      shippingInfoFormValues,
      billingInfoFormValues,
      onRequestToPaymetric,
      onSaveInitialSubscription,
      onCompleteInitialSubscription,
      onShowError,
      createAlert,
    }) => ({ shippingCorrectedResult, billingCorrectedResult }) => {
      const paymetricRequestData = {
        paymentCreditCard: _get(cardInfoFormValues, CARD_FIELD_NAMES.paymentCreditCard).replace(/-/g, ''),
        expMonth: _get(cardInfoFormValues, CARD_FIELD_NAMES.expMonth),
        expYear: _get(cardInfoFormValues, CARD_FIELD_NAMES.expYear),
        CVV: _get(cardInfoFormValues, CARD_FIELD_NAMES.CVV),
      }
      return onRequestToPaymetric(paymetricRequestData, cardInfoFormValues.captcha === undefined ? '' : cardInfoFormValues.captcha)
        .then(({ accessToken }) => {
          return onSaveInitialSubscription({
            userId,
            billingAddress: getNormalizedAddress({
              countryId: 1, // USA hardcoded
              ...billingInfoFormValues,
              ...billingCorrectedResult,
            }),
            company: businessInfoFormValues || null,
            shippingAddress: getNormalizedAddress({
              countryId: 1, // USA hardcoded
              ...shippingInfoFormValues,
              ...shippingCorrectedResult,
            }),
            creditCard: {
              paymentAccessToken: accessToken,
              cardHolderName: _get(cardInfoFormValues, CARD_FIELD_NAMES.nameOnCard).trim(),
              PreAuthCompleted: true,
            },
          })
            .then(({ value: { result: { shippingAddress, creditCard: { billingAddress } } } }) => {
              if (!shippingAddress.isMatch || !billingAddress.isMatch) {
                !shippingAddress.isMatch && createAlert({ name: ALERTS.profileShippingFormError })
                !billingAddress.isMatch && createAlert({ name: ALERTS.profileBillingFormError })
                return
              }
              return onCompleteInitialSubscription()
            })
            .catch(error => {
              onShowError(error)
              analytics.showPaymentAndShippingError({ errorCodes: getErrorCodes(error) })
            })
        })
        .catch((error: any) => {
          const errorCodes = getErrorCodes(error)
          analytics.showPaymentAndShippingError({
            errorCodes: getNormalizedValidationErrors(error.result).map(({ key, code }) => {
              if (code === ERROR_CODES.addressIsNotValid) {
                return `${code}.${key}`
              }
              return code
            }),
          })
          if (error.status === 409 && errorCodes.includes(ERROR_CODES.paymetricServiceUnavailable)) {
            openModal({
              name: MODAL_PAYMETRIC_SERVICE_UNAVAILABLE,
              data: {
                requestData: {
                  ...cardInfoFormValues,
                },
              },
            })
            return
          }
          else if (errorCodes.includes(ERROR_CODES.paymetricCvvCheckFailed) ||
                   errorCodes.includes(ERROR_CODES.paymetricAvsCheckFailed)) {
            createAlert({ name: ALERTS.profileCreditCardIssue })
            return
          }
          if (errorCodes.includes(ERROR_CODES.addressIsNotValid)) {
            const normalizedValidationErrors = getNormalizedValidationErrors(error.result)
            normalizedValidationErrors.forEach(({ code, key }) => {
              if (code === ERROR_CODES.addressIsNotValid) {
                key.includes('ShippingAddress') && onShowAddressFormsError(error, ALERTS.profileShippingFormError)
                key.includes('BillingAddress') && onShowAddressFormsError(error, ALERTS.profileBillingFormError)
              }
            })
            return
          }
          onShowError(error, cardInfoFormValues)
        })
    },
  }),
)(SignUpForms)
